import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { vendorGetShop, vendorUpdateBusinessProfile } from "@/lib/vendorApi";
import { TEMPLATE_INFO, FACILITY_LABELS } from "@/components/profile/BusinessProfileSections";

const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-w-0 focus:outline-none focus:border-emerald-500";
const blankFacilities = () => Object.fromEntries(Object.keys(FACILITY_LABELS).map((k) => [k, { available: null, details: "" }]));

function Rows({ items, setItems, fields, blank, max, testid, cols }) {
  const upd = (i, k, v) => setItems(items.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      {items.map((r, i) => (
        <div key={i} className={`grid grid-cols-1 ${cols} gap-2 rounded-xl bg-slate-50 p-2`} data-testid={`${testid}-row-${i}`}>
          {fields.map(([k, ph, max2, type]) => (
            <input key={k} type={type || "text"} value={r[k] ?? ""} maxLength={max2} placeholder={ph} className={inp} data-testid={`${testid}-${k}-${i}`}
              onChange={(e) => upd(i, k, type === "number" ? (e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0)) : e.target.value)} />
          ))}
          <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="inline-flex items-center justify-center rounded-lg border border-rose-200 text-rose-700 px-3 py-2 min-h-[40px]" aria-label="Remove" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {items.length < max && (
        <button type="button" onClick={() => setItems([...items, blank])} className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 min-h-[36px]" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /> Add</button>
      )}
    </div>
  );
}

// Owner editor for the isolated business-profile prototype (only rendered for shops with business_profile_enabled).
export default function BusinessProfileEditor({ shopId }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    vendorGetShop(shopId).then((s) => {
      const bp = s.business_profile || {};
      setP({ template: bp.template || "hospital", doctors: bp.doctors || [], tests: bp.tests || [], facilities: { ...blankFacilities(), ...(bp.facilities || {}) }, appointment_whatsapp: bp.appointment_whatsapp || "" });
    }).catch(() => toast.error("Could not load business profile"));
  }, [shopId]);
  if (!p) return <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading business profile…</div>;
  const t = TEMPLATE_INFO[p.template];
  const set = (k) => (v) => setP({ ...p, [k]: v });
  const setFac = (k, patch) => setP({ ...p, facilities: { ...p.facilities, [k]: { ...p.facilities[k], ...patch } } });
  const save = async () => {
    setSaving(true);
    try {
      const { updated_at, ...body } = p;
      body.doctors = body.doctors.filter((d) => d.name?.trim());
      body.tests = body.tests.filter((x) => x.name?.trim());
      await vendorUpdateBusinessProfile(shopId, body);
      setP({ ...p, doctors: body.doctors, tests: body.tests });
      toast.success("Business profile saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check the entered details (length / phone number)" : d || "Could not save");
    } finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-6 space-y-5 min-w-0" data-testid="business-profile-editor">
      <div>
        <h2 className="font-display text-xl font-bold">Business profile (test prototype)</h2>
        <p className="text-xs text-slate-500 mt-1">Only enter details your business confirms. Do not add patient names or medical records. Appointment requests go straight to your WhatsApp; nothing is saved in VerB.</p>
      </div>
      <label className="block text-sm font-semibold">Template
        <select value={p.template} onChange={(e) => set("template")(e.target.value)} className={`${inp} mt-1`} data-testid="bp-template-select">
          {Object.entries(TEMPLATE_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </label>
      <div><h3 className="text-sm font-bold mb-2">{t.doctors}</h3>
        <Rows items={p.doctors} setItems={set("doctors")} max={50} testid="bp-edit-doctor" cols="sm:grid-cols-[1fr_1fr_1fr_1fr_auto]" blank={{ name: "", department: "", specialty: "", opd_timings: "" }}
          fields={[["name", "Name", 80], ["department", "Department", 60], ["specialty", "Specialty", 80], ["opd_timings", "OPD timings", 120]]} />
      </div>
      <div><h3 className="text-sm font-bold mb-2">{t.tests}</h3>
        <Rows items={p.tests} setItems={set("tests")} max={100} testid="bp-edit-test" cols="sm:grid-cols-[2fr_1fr_1fr_auto]" blank={{ name: "", category: "", price: null }}
          fields={[["name", "Test / scan name", 100], ["category", "Category", 60], ["price", "Price ₹ (blank = ask)", 7, "number"]]} />
        <p className="text-[11px] text-slate-500 mt-1">Leave price blank to show “Contact for price”.</p>
      </div>
      <div><h3 className="text-sm font-bold mb-2">{t.facilities}</h3>
        <div className="space-y-2">
          {Object.entries(FACILITY_LABELS).map(([k, label]) => (
            <div key={k} className="grid grid-cols-1 sm:grid-cols-[170px_200px_1fr] gap-2 items-center rounded-xl bg-slate-50 p-2" data-testid={`bp-edit-facility-${k}`}>
              <div className="text-sm font-semibold">{label}</div>
              <select value={p.facilities[k].available === null ? "" : String(p.facilities[k].available)} className={inp} data-testid={`bp-edit-facility-${k}-status`}
                onChange={(e) => setFac(k, { available: e.target.value === "" ? null : e.target.value === "true" })}>
                <option value="">Not specified (hidden)</option><option value="true">Available</option><option value="false">Not available</option>
              </select>
              <input value={p.facilities[k].details} maxLength={200} placeholder="Details (optional)" className={inp} onChange={(e) => setFac(k, { details: e.target.value })} data-testid={`bp-edit-facility-${k}-details`} />
            </div>
          ))}
        </div>
      </div>
      <label className="block text-sm font-semibold">Appointment WhatsApp number
        <input value={p.appointment_whatsapp} inputMode="numeric" maxLength={12} placeholder="10-digit mobile number" className={`${inp} mt-1`} onChange={(e) => set("appointment_whatsapp")(e.target.value.replace(/\D/g, ""))} data-testid="bp-edit-whatsapp" />
      </label>
      <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold" data-testid="bp-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save business profile
      </button>
    </div>
  );
}
